import { ArrowRight } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Helmet } from "react-helmet-async"
import { useNavigate } from "react-router-dom"

export default function Valeurs() {
    const navigate = useNavigate()
    const stats = [
        { val: "5", label: "Valeurs Fondamentales" },
        { val: "PAIX", label: "Devise" },
        { val: "SAVOIR", label: "Devise" },
        { val: "DÉVELOPPEMENT", label: "Devise" }
    ]

    const values = [
        "Engagement à la paix et à la multiculturalité",
        "Recherche de l'intégration sociale",
        "Vocation à la coopération internationale",
        "Qualité de la formation",
        "Promotion de l'esprit d'entreprise"
    ]

    return (
        <div className="min-h-screen bg-background font-sans text-foreground">
            <Helmet>
                <title>Nos Valeurs - Université de Ngozi</title>
                <meta name="description" content="Les valeurs de l'UNG : engagement à la paix et à la multiculturalité, intégration sociale, coopération internationale, qualité de la formation, esprit d'entreprise." />
            </Helmet>

            {/* Hero Section */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/95" />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_70%_40%,rgba(255,255,255,0.06),transparent)]" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Notre Identité</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Valeurs & Devise</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Les Valeurs qui</span>
                            <span className="block text-white mt-2">Nous Définissent</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Les valeurs sont l'expression de la cohérence avec l'identité et le système organisationnel de l'institution.
                            </p>
                        </div>
                        <div className="inline-block relative group mb-8">
                            <div className="absolute -inset-1 bg-gradient-to-r from-accent via-primary to-accent rounded-2xl blur opacity-25 group-hover:opacity-100 transition duration-1000"></div>
                            <div className="relative px-8 py-6 bg-white/10 backdrop-blur-sm ring-1 ring-white/20 rounded-2xl">
                                <p className="text-3xl font-bold font-serif italic text-accent">"Paix, Savoir, Développement"</p>
                                <p className="text-primary-foreground/60 text-xs font-medium uppercase tracking-wider mt-2">Notre Devise</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button onClick={() => navigate('/mission')} className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Notre Mission
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats bar */}
            <div className="border-y border-border bg-secondary/30">
                <div className="container py-8 flex flex-wrap items-center justify-center gap-x-16 gap-y-6 lg:gap-x-24">
                    {stats.map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-3xl sm:text-4xl font-black text-primary tabular-nums">{stat.val}</span>
                            <span className="text-[11px] text-muted-foreground uppercase font-bold tracking-[0.25em]">{stat.label}</span>
                            {i < stats.length - 1 && <span className="hidden lg:block w-px h-8 bg-border ml-4" />}
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Values Section */}
            <article className="container py-24 lg:py-32">
                <div className="max-w-3xl mx-auto">
                    <div className="mb-6">
                        <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-accent">Définition</span>
                    </div>
                    <p className="text-xl md:text-2xl text-foreground leading-relaxed mb-16 font-light">
                        Les valeurs sont des attitudes comportementales conséquentes à la vision et à la mission d'une institution. Elles sont l'expression de la cohérence avec les éléments du système organisationnel qui découle de l'identité même de l'institution en question.
                    </p>
                    <div className="h-px w-20 bg-accent mb-10" />
                    <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-10">L'université a comme valeurs :</h2>
                    <ul className="space-y-0">
                        {values.map((value, idx) => (
                            <li
                                key={idx}
                                className="flex gap-5 py-6 pl-6 pr-4 border-l-4 border-transparent border-b border-border/50 last:border-b-0 hover:border-l-primary/40 hover:bg-secondary/30 transition-colors duration-200 first:pt-6 last:pb-6 rounded-r-lg"
                            >
                                <span
                                    className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 text-primary font-bold text-base flex items-center justify-center"
                                    aria-hidden
                                >
                                    {idx + 1}
                                </span>
                                <span className="text-foreground text-lg md:text-xl leading-relaxed pt-1.5">{value}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-16 pt-12 border-t border-border">
                        <Button onClick={() => navigate('/mission')} className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-base font-semibold">
                            Mission & Vision
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                </div>
            </article>

            {/* Immersive CTA Footer */}
            <footer className="bg-primary text-white border-t-8 border-accent relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/5 -skew-x-12 origin-top-right pointer-events-none" />
                <div className="container py-24 text-center relative z-10">
                    <div className="max-w-2xl mx-auto space-y-10">
                        <div className="inline-flex items-center gap-6 mb-4">
                            <div className="h-px w-8 bg-accent" />
                            <span className="text-[11px] uppercase tracking-[0.4em] text-accent font-black">Vivez Nos Valeurs</span>
                            <div className="h-px w-8 bg-accent" />
                        </div>

                        <h2 className="text-4xl lg:text-6xl font-serif italic mb-8 leading-tight">
                            Rejoignez une communauté <br /> <span className="text-accent underline decoration-accent/30 decoration-8 underline-offset-4">guidée par l'excellence</span>.
                        </h2>

                        {/* <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                            <Button onClick={() => navigate('/formation')} className="bg-accent text-accent-foreground px-12 py-8 text-sm font-black hover:bg-white hover:text-primary hover:-translate-y-2 transition-all duration-500 rounded-2xl transform shadow-2xl">
                                Découvrir Nos Programmes
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button variant="ghost" onClick={() => navigate('/contact')} className="text-white border-2 border-white/20 hover:bg-white/5 px-12 py-8 text-sm font-black rounded-2xl transition-all">
                                Nous Contacter
                            </Button>
                        </div> */}

                        <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-medium mt-12">
                            © 2024 Université de Ngozi · Valeurs Institutionnelles
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    )
}
